/**
 * Shared default card order actions for the open pipeline (SE-6863).
 *
 * A pipeline stage has a shared default card order that every viewer without a
 * personal order sees, and a personal order (built by dragging cards within a
 * stage) that wins for the viewer who holds it. This hook supplies the two
 * board-toolbar overflow rows that move between them:
 *
 * - "Set as team default order" (Manager+ only) publishes the order the viewer
 *   currently sees as the shared default. The server also clears the viewer's
 *   own personal order, so they follow the default from then on.
 * - "Reset to team default order" (anyone with a personal order) drops the
 *   personal order so the viewer sees the shared default again.
 *
 * Rows rather than a new component for the reason `useBoardToolbar` gives: a
 * hook adds no renderable surface, so the header stays design-system
 * conformant. DESIGN-SOURCE: none — not in Figma (two text rows in the
 * existing, already-undesigned board overflow menu; see SE-6488).
 * @module features/player-kanban/hooks/useBoardCardOrderActions
 */

import { RotateCcw, Users } from "lucide-react-native";
import { useCallback, useEffect, useMemo } from "react";

import type { DesktopDropdownMenuItem } from "@/components/molecules/DesktopDropdownMenu";
import {
  useGetKanbanBoardCardOrderStateQuery,
  useResetKanbanBoardCardOrderMutation,
  useSetKanbanBoardDefaultCardOrderMutation,
} from "@/generated/graphql";
import { useToastNotification } from "@/hooks/useToastNotification";
import { TEST_ID_BOARD_TOOLBAR } from "e2e/testId";

import { MESSAGES } from "../constants";
import { useIsPipelineManager } from "./useIsPipelineManager";

/** Queries whose answer changes when the shared or personal order changes. */
export const CARD_ORDER_REFETCH_QUERIES = [
  "ListKanbanCards",
  "GetKanbanBoardCardOrderState",
];

/** Inputs for {@link useBoardCardOrderActions}. */
export interface UseBoardCardOrderActionsArgs {
  /** The open board. */
  readonly boardId?: string;
  /** Whether the open board is a PIPELINE board; nothing is offered otherwise. */
  readonly isPipelineBoard: boolean;
  /** Whether the overflow menu is open; opening it re-reads the flag. */
  readonly isMenuOpen: boolean;
  /**
   * Whether the board shows its server order, meaning no toolbar sort is
   * applied. Publishing sends the viewer's SERVER rank order, so while a
   * client-side sort re-orders the cards on screen, "Set as team default"
   * would publish an order the viewer is not looking at; it is not offered.
   */
  readonly isServerOrder: boolean;
}

/** What {@link useBoardCardOrderActions} returns. */
export interface UseBoardCardOrderActionsResult {
  /** Overflow rows to append to the board toolbar menu. */
  readonly items: readonly DesktopDropdownMenuItem[];
  /** Whether the viewer currently keeps a personal card order on the board. */
  readonly hasPersonalCardOrder: boolean;
}

/**
 * Drive the shared default card order rows of the board toolbar menu.
 * @param args - See {@link UseBoardCardOrderActionsArgs}.
 * @param args.boardId - The open board.
 * @param args.isPipelineBoard - Whether the open board is a PIPELINE board.
 * @param args.isMenuOpen - Whether the overflow menu is open.
 * @param args.isServerOrder - Whether no toolbar sort re-orders the board.
 * @returns The {@link UseBoardCardOrderActionsResult}.
 */
export function useBoardCardOrderActions({
  boardId,
  isPipelineBoard,
  isMenuOpen,
  isServerOrder,
}: UseBoardCardOrderActionsArgs): UseBoardCardOrderActionsResult {
  const isManager = useIsPipelineManager();
  const toast = useToastNotification();
  const isActive = isPipelineBoard && Boolean(boardId);

  const { data, refetch } = useGetKanbanBoardCardOrderStateQuery({
    variables: { id: boardId ?? "" },
    skip: isActive === false,
    fetchPolicy: "cache-and-network",
  });

  // Refetches are NOT awaited: the mutation response already carries every
  // card with its new position and rank, and an awaited refetch that fails
  // would reject the mutation and show an error for a publish that succeeded.
  const mutationOptions = {
    refetchQueries: CARD_ORDER_REFETCH_QUERIES,
  };
  const [setDefaultOrder] =
    useSetKanbanBoardDefaultCardOrderMutation(mutationOptions);
  const [resetOrder] = useResetKanbanBoardCardOrderMutation(mutationOptions);

  const onSetDefaultOrder = useCallback(async () => {
    if (boardId === undefined) return;
    try {
      await setDefaultOrder({ variables: { kanbanBoardId: boardId } });
      toast.show({
        action: "success",
        description: "Team default order updated",
      });
    } catch {
      toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
    }
  }, [boardId, setDefaultOrder, toast]);

  const onResetOrder = useCallback(async () => {
    if (boardId === undefined) return;
    try {
      await resetOrder({ variables: { kanbanBoardId: boardId } });
      toast.show({
        action: "success",
        description: "Showing the team default order",
      });
    } catch {
      toast.show({ action: "danger", description: MESSAGES.ERROR.GENERIC });
    }
  }, [boardId, resetOrder, toast]);

  const hasPersonalCardOrder =
    isActive && data?.getKanbanBoard?.hasPersonalCardOrder === true;

  const items = useMemo<DesktopDropdownMenuItem[]>(() => {
    if (isActive === false) return [];
    const candidates: readonly (DesktopDropdownMenuItem | null)[] = [
      // Manager+ only, mirroring the server check: a sub-Manager is never
      // offered an action the API would refuse. And only while the board shows
      // its server order, since that is the order the mutation publishes.
      isManager && isServerOrder
        ? {
            id: "set-default-card-order",
            label: "Set as team default order",
            icon: Users,
            testID: TEST_ID_BOARD_TOOLBAR.OVERFLOW_SET_DEFAULT_ORDER,
            onPress: () => void onSetDefaultOrder(),
          }
        : null,
      hasPersonalCardOrder
        ? {
            id: "reset-card-order",
            label: "Reset to team default order",
            icon: RotateCcw,
            testID: TEST_ID_BOARD_TOOLBAR.OVERFLOW_RESET_ORDER,
            onPress: () => void onResetOrder(),
          }
        : null,
    ];
    return candidates.filter(
      (item): item is DesktopDropdownMenuItem => item !== null
    );
  }, [
    isActive,
    isManager,
    isServerOrder,
    hasPersonalCardOrder,
    onSetDefaultOrder,
    onResetOrder,
  ]);

  // A drag since the last read may have created a personal order, so the flag
  // is re-read every time the menu opens rather than trusted from the cache.
  useEffect(() => {
    if (isMenuOpen && isActive) {
      void refetch();
    }
  }, [isMenuOpen, isActive, refetch]);

  return { items, hasPersonalCardOrder };
}
